<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import { useCopyToClipboard } from '@/composables/useCopyToClipboard';
import { formatDateForDisplay } from '@/utils/datetimeUtils';
import type { ClauseLibrary, LibraryClause } from '@/services/clauseLibraryService';
import GaiaButton from '../../base/GaiaButton.vue';
import GaiaDeleteButton from '../../base/GaiaDeleteButton.vue';
import GaiaDrawer from '../../base/GaiaDrawer.vue';
import GaiaFieldRow from '../../base/GaiaFieldRow.vue';
import GaiaHeader from '../../base/GaiaHeader.vue';
import GaiaTag from '../../base/GaiaTag.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaSubPanel from '../../composite/GaiaSubPanel.vue';
import GaiaClauseText from './GaiaClauseText.vue';
import { clauseCategoryMap, contractTypeNames } from '../../statusMaps';
import { gaiaNeutral } from '../../themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  /** The clause on show. `null` while the drawer is closing. */
  clause: {
    type: Object as PropType<LibraryClause | null>,
    default: null,
  },
  library: {
    type: String as PropType<ClauseLibrary>,
    default: 'organisation',
  },
  /** Whether this viewer may edit and delete in `library`. */
  canWrite: {
    type: Boolean,
    default: false,
  },
  /**
   * Offer "Copy to organisation" — a GAIA clause a viewer may not edit here but
   * may take a copy of into their own library.
   */
  canCopyToOrganisation: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  'update:show': [value: boolean];
  edit: [clause: LibraryClause];
  delete: [clause: LibraryClause];
  'copy-to-organisation': [clause: LibraryClause];
}>();

const message = useGaiaToast();
const { copy } = useCopyToClipboard();
const copied = ref(false);

const isManaged = computed(() => props.library === 'gaia');

/**
 * Created / updated only.
 *
 * The mock also names an owner, but neither clause table carries one — the row
 * knows its organisation, not the person who wrote it — so inventing one here
 * would be the drawer's only untrue field.
 */
const meta = computed(() => {
  const clause = props.clause;
  if (!clause) return '';
  const parts = [`Created ${formatDateForDisplay(clause.created_at)}`];
  if (clause.updated_at) parts.push(`updated ${formatDateForDisplay(clause.updated_at)}`);
  if (isManaged.value) parts.push('maintained by Gaia');
  return parts.join(' · ');
});

const contractTypes = computed(() => {
  const names = contractTypeNames(props.clause?.contract_types);
  return names.length ? names.join(', ') : '—';
});

const category = computed(() => {
  const value = props.clause?.clause_category;
  return value ? clauseCategoryMap[value] : '—';
});

const primaryLabel = computed(() => (isManaged.value ? 'Copy to organisation' : 'Edit clause'));

const showPrimary = computed(() =>
  isManaged.value ? props.canCopyToOrganisation : props.canWrite
);

// A fresh clause deserves a fresh Copy button — "Copied" left over from the
// previous one would claim something untrue about this one.
watch(
  () => [props.show, props.clause?.uid] as const,
  () => {
    copied.value = false;
  }
);

function close(): void {
  emit('update:show', false);
}

async function copyText(): Promise<void> {
  const clause = props.clause;
  if (!clause) return;
  if (await copy(clause.content)) {
    copied.value = true;
    return;
  }
  message.error('Could not copy the clause text.');
}

function onPrimary(): void {
  const clause = props.clause;
  if (!clause) return;
  if (isManaged.value) {
    emit('copy-to-organisation', clause);
    return;
  }
  emit('edit', clause);
}

const gridBorder = gaiaNeutral.borderCard;
</script>

<template>
  <!-- The read side of a clause: what it is, then the wording itself. The
       wording is the reason anyone opened this, so it gets the serif panel and
       the Copy button rather than a field row. -->
  <gaia-drawer
    :show="props.show"
    title="Clause"
    :width="620"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <n-flex v-if="props.clause" vertical :size="20" :wrap="false">
      <n-flex vertical :size="8" :wrap="false">
        <n-flex align="center" :size="9">
          <gaia-header :level="2" variant="display" size="dialog">
            {{ props.clause.name }}
          </gaia-header>
          <gaia-tag v-if="isManaged" variant="accent" size="tiny">Gaia managed</gaia-tag>
        </n-flex>
        <gaia-text size="control" tone="subtle">{{ meta }}</gaia-text>
      </n-flex>

      <div class="field-grid">
        <gaia-field-row label="Category" variant="plain" :label-width="132">
          {{ category }}
        </gaia-field-row>
        <gaia-field-row label="Contract types" variant="plain" :label-width="132">
          {{ contractTypes }}
        </gaia-field-row>
        <gaia-field-row label="Jurisdiction" variant="plain" :label-width="132">
          {{ props.clause.jurisdiction || '—' }}
        </gaia-field-row>
        <gaia-field-row label="Language" variant="plain" :label-width="132">
          {{ props.clause.language || '—' }}
        </gaia-field-row>
        <gaia-field-row label="Source" variant="plain" :label-width="132">
          {{ props.clause.source || '—' }}
        </gaia-field-row>
        <gaia-field-row v-if="isManaged" label="Maintained by" variant="plain" :label-width="132">
          Gaia managed library
        </gaia-field-row>
      </div>

      <gaia-sub-panel label="Clause text">
        <template #actions>
          <gaia-button size="tiny" secondary @click="copyText">
            {{ copied ? 'Copied' : 'Copy' }}
          </gaia-button>
        </template>
        <gaia-clause-text :text="props.clause.content" tone="plain" />
      </gaia-sub-panel>
    </n-flex>

    <template #footer>
      <n-flex align="center" :size="10" :wrap="false" :style="{ width: '100%' }">
        <gaia-delete-button
          v-if="props.canWrite && props.clause"
          variant="label"
          @click="emit('delete', props.clause)"
        />
        <n-flex
          align="center"
          :size="10"
          :wrap="false"
          :style="{ marginLeft: 'auto', flex: 'none' }"
        >
          <gaia-button quaternary @click="close">Close</gaia-button>
          <gaia-button v-if="showPrimary" type="primary" @click="onPrimary">
            {{ primaryLabel }}
          </gaia-button>
        </n-flex>
      </n-flex>
    </template>
  </gaia-drawer>
</template>

<style scoped>
/* The rows are a card, not a page section: one hairline box around them, and no
   divider under the last one. GaiaFieldRow draws its own bottom border and has
   no "last row" awareness, so the box and the override live here. */
.field-grid {
  border: 1px solid v-bind(gridBorder);
  border-radius: 11px;
  padding: 0 14px;
}

.field-grid > :last-child {
  border-bottom: none;
}
</style>
